import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorCount: 0,
      lastError: null
    };
  }

  static getDerivedStateFromError(error) {
    // تحديث الحالة لإظهار واجهة الخطأ
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // تحديث عداد الأخطاء وتخزين آخر خطأ
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
      lastError: new Date().toISOString()
    }));

    // تسجيل الخطأ في وحدة التحكم
    console.error('Error caught by boundary:', error, errorInfo);
    
    // يمكن إرسال الخطأ إلى خدمة تتبع الأخطاء هنا
    this.logErrorToService(error, errorInfo);
  }

  // إرسال الخطأ إلى خدمة تتبع الأخطاء
  logErrorToService(error, errorInfo) {
    // تنفيذ فقط في بيئة الإنتاج
    if (process.env.NODE_ENV === 'production') {
      // يمكن استخدام خدمات مثل Sentry أو LogRocket هنا
      // مثال: Sentry.captureException(error);
      
      // إرسال بيانات الخطأ إلى الخادم (تعليق حتى يتم تنفيذ الخدمة)
      /*
      fetch('/api/log-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error.toString(),
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      }).catch(err => console.error('Failed to log error:', err));
      */
    }
  }

  // إعادة تعيين حالة الخطأ
  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  }

  // محاولة إعادة تحميل المكون تلقائيًا بعد فترة
  retryAfterDelay = (delay = 2000) => {
    setTimeout(() => {
      this.resetError();
    }, delay);
  }

  render() {
    if (this.state.hasError) {
      // إعادة المحاولة تلقائيًا إذا كان عدد الأخطاء أقل من 3
      if (this.state.errorCount < 3) {
        this.retryAfterDelay();
        return <LoadingFallback />;
      }
      
      return <ErrorFallback 
        error={this.state.error} 
        errorInfo={this.state.errorInfo}
        errorCount={this.state.errorCount}
        lastError={this.state.lastError}
        resetError={this.resetError}
      />;
    }

    return this.props.children;
  }
}

// مكون الانتظار أثناء إعادة المحاولة
const LoadingFallback = () => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      textAlign: 'center'
    }}>
      <div className="loading-spinner" style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #2196f3',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      <p>إعادة تحميل المكون...</p>
    </div>
  );
};

// مكون واجهة الخطأ
const ErrorFallback = ({ error, errorInfo, errorCount, lastError, resetError }) => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  
  const isArabic = language === 'ar';
  const isDark = theme === 'dark';
  
  return (
    <div style={{
      padding: '20px',
      margin: '20px',
      border: '1px solid #f44336',
      borderRadius: '8px',
      backgroundColor: isDark ? '#2c2c2c' : '#ffebee',
      color: isDark ? '#ffffff' : '#333333',
      textAlign: isArabic ? 'right' : 'left',
      direction: isArabic ? 'rtl' : 'ltr',
      fontFamily: 'Cairo, sans-serif',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
    }}>
      <h2 style={{ color: '#f44336', marginBottom: '16px' }}>
        {isArabic ? '⚠️ حدث خطأ غير متوقع' : '⚠️ Something went wrong'}
      </h2>
      
      <p style={{ marginBottom: '16px', color: isDark ? '#cccccc' : '#666666' }}>
        {isArabic 
          ? 'نعتذر، حدث خطأ أثناء تحميل هذه الصفحة. يرجى المحاولة مرة أخرى.'
          : 'Sorry, an error occurred while loading this page. Please try again.'
        }
      </p>
      
      <div style={{ marginBottom: '16px' }}>
        <button 
          onClick={resetError}
          style={{
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: isArabic ? '0' : '10px',
            marginLeft: isArabic ? '10px' : '0',
            transition: 'background-color 0.3s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#0b7dda'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#2196f3'}
        >
          {isArabic ? 'إعادة المحاولة' : 'Try Again'}
        </button>
        
        <button 
          onClick={() => window.location.href = '/'}
          style={{
            backgroundColor: isDark ? '#555555' : '#666666',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = isDark ? '#777777' : '#888888'}
          onMouseOut={(e) => e.target.style.backgroundColor = isDark ? '#555555' : '#666666'}
        >
          {isArabic ? 'العودة للرئيسية' : 'Go Home'}
        </button>
      </div>
      
      {/* معلومات إضافية عن الخطأ */}
      <div style={{ 
        fontSize: '12px', 
        color: isDark ? '#aaaaaa' : '#888888',
        marginTop: '20px',
        padding: '10px',
        borderTop: `1px solid ${isDark ? '#444444' : '#dddddd'}`
      }}>
        <p>{isArabic ? 'عدد محاولات إعادة التحميل:' : 'Retry count:'} {errorCount}</p>
        {lastError && <p>{isArabic ? 'آخر خطأ:' : 'Last error:'} {new Date(lastError).toLocaleString()}</p>}
      </div>
      
      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginTop: '16px' }}>
          <summary style={{ cursor: 'pointer', color: isDark ? '#aaaaaa' : '#666666' }}>
            {isArabic ? 'تفاصيل الخطأ (للمطورين)' : 'Error Details (for developers)'}
          </summary>
          <pre style={{ 
            backgroundColor: isDark ? '#1c1c1c' : '#f5f5f5', 
            color: isDark ? '#cccccc' : '#333333',
            padding: '10px', 
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
            marginTop: '8px'
          }}>
            {error && error.toString()}
            <br />
            {errorInfo && errorInfo.componentStack}
          </pre>
        </details>
      )}
    </div>
  );
};

// إضافة CSS للتحميل
if (!document.querySelector('#error-boundary-styles')) {
  const style = document.createElement('style');
  style.id = 'error-boundary-styles';
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default ErrorBoundary;